/**
 * Service Worker Helper Utilities
 * Helps manage service worker state and prevents OAuth issues during installation
 */

/**
 * Check if service worker is ready for navigation
 * @returns {Promise<boolean>} True if service worker is ready or not present
 */
export const isServiceWorkerReady = async (): Promise<boolean> => {
  if (!('serviceWorker' in navigator)) {
    return true; // No service worker, so it's "ready"
  }

  try {
    const registration = await navigator.serviceWorker.getRegistration();

    if (!registration) {
      return true; // No registration, so it's "ready"
    }

    // Consider it ready if it's active or if there's no service worker at all
    return registration.active !== null || registration.installing === null;
  } catch (error) {
    console.warn('Service worker ready check failed:', error);
    return true; // Assume ready on error
  }
};

/**
 * Wait for service worker to be ready
 * @param {number} timeout - Maximum time to wait in milliseconds
 * @returns {Promise<boolean>} True if ready within timeout
 */
export const waitForServiceWorkerReady = async (timeout: number = 5000): Promise<boolean> => {
  const startTime = Date.now();

  while (Date.now() - startTime < timeout) {
    if (await isServiceWorkerReady()) {
      return true;
    }

    // Wait 100ms before checking again
    await new Promise(resolve => setTimeout(resolve, 100));
  }

  console.warn('Service worker ready timeout reached');
  return false;
};

